// argon2-browser is loaded via CDN script tag in app.html
// This avoids Vite bundling issues with the WASM module

// Check for browser environment
const isBrowser = typeof window !== 'undefined';

// Declare the global argon2 type
declare global {
  interface Window {
    argon2: {
      hash: (params: {
        pass: string | Uint8Array;
        salt: string | Uint8Array;
        time: number;
        mem: number;
        parallelism: number;
        hashLen: number;
        type: number;
      }) => Promise<{ hash: Uint8Array; hashHex: string; encoded: string }>;
      ArgonType: {
        Argon2d: number;
        Argon2i: number;
        Argon2id: number;
      };
    };
  }
}

function getArgon2() {
  if (!isBrowser) {
    throw new Error('argon2 can only be used in browser environment');
  }
  if (!window.argon2) {
    throw new Error('argon2 not loaded - check that CDN script is included in app.html');
  }
  return window.argon2;
}

// Parameters for encryption key derivation (high security)
// Note: type is set dynamically after import
const ENCRYPTION_PARAMS = {
  time: 3,           // iterations
  mem: 65536,        // 64 MiB memory
  parallelism: 1,    // single-threaded for compatibility
  hashLen: 32,       // 256-bit key
};

// Parameters for password hashing (auth, lower cost for Workers CPU limits)
const AUTH_PARAMS = {
  time: 2,           // iterations
  mem: 19456,        // 19 MiB memory (OWASP minimum)
  parallelism: 1,
  hashLen: 32,
};

/**
 * Derive encryption key from passphrase using Argon2id.
 * Uses high-security parameters (64MB, 3 iterations).
 */
export async function deriveEncryptionKey(
  passphrase: string,
  salt: Uint8Array
): Promise<Uint8Array> {
  const argon2 = getArgon2();
  const result = await argon2.hash({
    pass: passphrase,
    salt: salt,
    ...ENCRYPTION_PARAMS,
    type: argon2.ArgonType.Argon2id,
  });
  return result.hash;
}

/**
 * Hash password for authentication using Argon2id.
 * Uses lower-cost parameters suitable for client-side hashing.
 */
export async function hashPassword(
  password: string,
  salt: Uint8Array
): Promise<Uint8Array> {
  const argon2 = getArgon2();
  const result = await argon2.hash({
    pass: password,
    salt: salt,
    ...AUTH_PARAMS,
    type: argon2.ArgonType.Argon2id,
  });
  return result.hash;
}

/**
 * Generate a random salt for key derivation.
 */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}
